import { useUserAuth } from '../providers/auth';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import { useApolloClient } from '@apollo/client';
import { Alert, Button, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiHost } from '@pv/common/api';
import {
  AssistantConversation,
  AssistantEntry,
  mergeTimeline,
} from '@pv/common/assistants';
import { GET_CONVERSATION } from './graphql';
import { readAssistantStream } from './stream';

export class AssistantSessions {
  snapshots = new Map<string, AssistantConversation>();
  errors = new Map<string, string>();
  connections = new Map<string, AbortController>();
  liveRuns = new Set<string>();
  handledActions = new Set<string>();
  activeView: string | null = null;
  openRequest: string | null = null;
  private version = 0;
  private listeners = new Set<() => void>();
  constructor(
    readonly browserSessionId: string,
    private history: (id: string) => Promise<AssistantConversation>
  ) {}
  subscribe = (callback: () => void) => {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  };
  getVersion = () => this.version;
  notify = () => {
    this.version += 1;
    this.listeners.forEach((listener) => listener());
  };
  adopt = (incoming: AssistantConversation) => {
    const previous = this.snapshots.get(incoming.id);
    if (
      previous &&
      (previous.revision > incoming.revision ||
        previous.contextId !== incoming.contextId)
    )
      return;
    this.snapshots.set(incoming.id, {
      ...incoming,
      timeline: mergeTimeline(previous?.timeline || [], incoming.timeline),
    });
    this.notify();
  };
  open = (id: string) => {
    this.openRequest = id;
    this.notify();
  };
  stop = (id: string) => this.connections.get(id)?.abort();
  dispose = () => {
    this.connections.forEach((controller) => controller.abort());
    this.snapshots.clear();
    this.errors.clear();
    this.liveRuns.clear();
    this.handledActions.clear();
  };
  start = (id: string, action: string, values: Record<string, unknown>) => {
    if (this.connections.has(id))
      return Promise.reject(
        new Error('Wait for the current response or stop it first.')
      );
    const controller = new AbortController();
    this.connections.set(id, controller);
    this.errors.delete(id);
    this.notify();
    return new Promise<AssistantConversation>((resolve, reject) => {
      let accepted = false;
      const consume = async () => {
        try {
          const response = await fetch(
            apiHost(process.env.REACT_APP_PV_ENV as string) +
              '/assistant/conversations/' +
              id +
              '/turns',
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
              },
              signal: controller.signal,
              body: JSON.stringify({
                action_name: action,
                browser_session_id: this.browserSessionId,
                ...values,
              }),
            }
          );
          if (!response.ok) {
            const data = await response.json();
            throw new Error(
              data.error || 'Assistant unavailable. Please try again.'
            );
          }
          if (!response.body)
            throw new Error('Streaming is unavailable in this browser.');
          await readAssistantStream(response.body, ({ type, data }) => {
            if (type === 'text') {
              const text = data as {
                conversationId: string;
                contextId: string;
                runId: string;
                entry: AssistantEntry;
              };
              const snapshot = this.snapshots.get(id);
              if (
                !snapshot ||
                text.conversationId !== id ||
                text.contextId !== snapshot.contextId ||
                text.runId !== snapshot.run?.id
              )
                return;
              this.snapshots.set(id, {
                ...snapshot,
                timeline: mergeTimeline(snapshot.timeline, [text.entry]),
              });
              this.notify();
            } else if (type === 'error') {
              throw new Error((data as { message: string }).message);
            } else if (
              [
                'accepted',
                'snapshot',
                'tool_update',
                'browser_action',
                'done',
              ].includes(type)
            ) {
              const snapshot = data as AssistantConversation;
              if (snapshot.id !== id)
                throw new Error('Unexpected conversation in assistant stream.');
              this.adopt(snapshot);
              if (type === 'accepted') {
                accepted = true;
                if (snapshot.run?.id) this.liveRuns.add(snapshot.run.id);
                resolve(snapshot);
              }
            }
          });
          if (!accepted)
            throw new Error('Assistant request was not acknowledged.');
        } catch (reason) {
          const error =
            reason instanceof Error
              ? reason
              : new Error('Assistant connection interrupted.');
          this.errors.set(
            id,
            error.name === 'AbortError' ? 'Work stopped.' : error.message
          );
          if (!accepted) reject(error);
          try {
            this.adopt(await this.history(id));
          } catch {
            this.snapshots.delete(id);
            this.errors.set(id, 'This conversation is unavailable.');
          }
        } finally {
          this.connections.delete(id);
          this.notify();
        }
      };
      void consume();
    });
  };
}

const Context = createContext<AssistantSessions | null>(null);

function UserAssistantSessionProvider({ children }: { children: ReactNode }) {
  const client = useApolloClient();
  const navigate = useNavigate();
  const storeRef = useRef<AssistantSessions | null>(null);
  if (!storeRef.current) {
    const key = 'assistant:browser';
    const session = sessionStorage.getItem(key) || crypto.randomUUID();
    sessionStorage.setItem(key, session);
    storeRef.current = new AssistantSessions(session, async (id) => {
      const { data } = await client.query({
        query: GET_CONVERSATION,
        variables: { id },
        fetchPolicy: 'network-only',
      });
      return data.assistantConversation;
    });
  }
  const store = storeRef.current;
  useSyncExternalStore(store.subscribe, store.getVersion);
  useEffect(() => () => store.dispose(), [store]);
  const pending = [...store.snapshots.values()].find(
    (snapshot) =>
      snapshot.run?.status === 'waiting' && snapshot.id !== store.activeView
  );
  return (
    <Context.Provider value={store}>
      {children}
      <Snackbar
        open={!!pending}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                if (!pending) return;
                store.open(pending.id);
                navigate('/events/' + pending.contextId + '/emails');
              }}
            >
              Return to chat
            </Button>
          }
        >
          The assistant needs your input.
        </Alert>
      </Snackbar>
    </Context.Provider>
  );
}

export function useAssistantSessions() {
  const store = useContext(Context);
  if (!store) throw new Error('AssistantSessionProvider is required');
  useSyncExternalStore(store.subscribe, store.getVersion);
  return store;
}

export function AssistantSessionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user } = useUserAuth();
  return (
    <UserAssistantSessionProvider key={user?.id || 'anonymous'}>
      {children}
    </UserAssistantSessionProvider>
  );
}
